import React, { useState, useRef, useLayoutEffect } from 'react';

// A wrapping row of stats that tells its contents whether they fit on one line.
// children is a function: (compact) => the row's items. compact is false while the items fit side by side,
// and true when they would wrap onto a second line (so the caller can show shorter numbers, e.g. 5.1k).
// It switches back to false as soon as the full-length items would fit again (on rotate / resize).
export default function FitRow({ className, children }) {
  const ref = useRef(null);
  const [compact, setCompact] = useState(false);
  const compactRef = useRef(false);
  const fullWidth = useRef(0); // how wide the items were when last shown full-length

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const items = Array.from(el.children);
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    // Exact (fractional) widths: whole-pixel rounding can make a row that wraps look like it fits
    const needed = items.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) + gap * Math.max(0, items.length - 1);
    const available = el.getBoundingClientRect().width;
    if (!compactRef.current) {
      fullWidth.current = needed;
      if (needed > available + 0.1) {
        compactRef.current = true;
        setCompact(true);
      }
    } else if (fullWidth.current && fullWidth.current <= available) {
      compactRef.current = false;
      setCompact(false);
    }
  };

  // After every render (the numbers can change) and whenever the row's width changes
  useLayoutEffect(measure);
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !ref.current) return;
    const observer = new ResizeObserver(measure);
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Full width, so its width is the space there is (not just the width of what's in it)
  return <div ref={ref} className={className} style={{ width: '100%' }}>{children(compact)}</div>;
}
